/**
 * Stripe-hosted Checkout returns the admin to a single, **org-agnostic** success
 * URL that is configured Lago-side — one per environment, and Lago passes it to
 * Stripe verbatim without appending any session/customer identifier (task 5.7;
 * verified against lago-api v1.46). So the return URL cannot carry the specific
 * org, and we recover it app-side instead: stash the org id here right before
 * the full-page Checkout redirect, then read it back on the generic
 * `/billing/checkout-return` route to forward the admin to that org's billing
 * page.
 *
 * `sessionStorage` so the value survives the full-page redirect to Stripe and
 * back, but not a new tab or a fresh session. Best-effort — a private-mode /
 * storage-disabled browser just misses the stash and hits the return route's
 * fallback.
 */
const CHECKOUT_RETURN_ORG_KEY = 'droidmap:checkout-return-org'

/** Record the org a checkout redirect belongs to, so the return can route back to it. */
export function rememberCheckoutReturnOrg(orgId: string): void {
  try {
    window.sessionStorage.setItem(CHECKOUT_RETURN_ORG_KEY, orgId)
  } catch {
    /* storage unavailable — the return route falls back to the org list */
  }
}

/**
 * Read-and-clear the stashed return org. Clearing on read means a stale value
 * can't misroute a later, unrelated Checkout return. Returns `null` when nothing
 * was stashed (cold load, new tab/session, or storage disabled).
 */
export function takeCheckoutReturnOrg(): string | null {
  try {
    const orgId = window.sessionStorage.getItem(CHECKOUT_RETURN_ORG_KEY)
    window.sessionStorage.removeItem(CHECKOUT_RETURN_ORG_KEY)
    return orgId || null
  } catch {
    return null
  }
}

/**
 * Whether the org already had a card on file when we sent the admin to Checkout
 * (#2668). Stashed beside {@link rememberCheckoutReturnOrg}, and for the same
 * reason: it is knowable only on THIS side of the redirect.
 *
 * It exists because nothing observable on the return can tell an added card
 * from a replaced one. `hasPaymentMethod` on the billing state is Lago's
 * `billing_configuration.payment_method_id` (`billing.ts:227` —
 * `customerState?.cardOnFile`, deliberately not the org row's set-only
 * `has_payment_method` column), so for an org that already had a card it reads
 * `true` before, during and after the replacement. Only the pre-redirect
 * reading separates the two cases, so it has to be captured then.
 *
 * `sessionStorage`, read-and-clear, best-effort — identical lifetime and
 * failure mode to the org stash above.
 */
const CHECKOUT_HAD_CARD_KEY = 'droidmap:checkout-had-card'

/** Record whether a card was already on file as the Checkout redirect leaves. */
export function rememberCheckoutHadPaymentMethod(hadCard: boolean): void {
  try {
    window.sessionStorage.setItem(CHECKOUT_HAD_CARD_KEY, hadCard ? '1' : '0')
  } catch {
    /* storage unavailable — the return falls back to hand-dismissal */
  }
}

/**
 * Read-and-clear that reading. `null` when nothing was stashed — a cold load, a
 * new tab/session, or storage disabled — which callers must treat as "unknown",
 * NOT as "no card": guessing wrong in that direction retires a banner that is
 * still telling the truth.
 */
export function takeCheckoutHadPaymentMethod(): boolean | null {
  try {
    const raw = window.sessionStorage.getItem(CHECKOUT_HAD_CARD_KEY)
    window.sessionStorage.removeItem(CHECKOUT_HAD_CARD_KEY)
    return raw === null ? null : raw === '1'
  } catch {
    return null
  }
}

/**
 * Whether the "Payment method submitted. It may take a moment to be confirmed."
 * banner has been overtaken by the state it describes, and should retire itself
 * (#2668).
 *
 * It was dismissible only by hand, so it went on advertising an unconfirmed
 * payment method for as long as the admin left the tab open — in the report
 * that filed this, a full day, beside a PAID invoice and an ACTIVE card. Same
 * family as the frozen notice above it: post-success copy outliving the state
 * it describes.
 *
 * **Only an org that had no card can be told it now has one.** That is the
 * whole predicate, and the reason it takes a pre-redirect reading rather than
 * only the current one. `hasPaymentMethod` is Lago's `payment_method_id`
 * (`billing.ts:227`), so on a card **replacement** it is `true` throughout —
 * before the redirect, on the first read back, and after the new card lands.
 * Retiring on the flag alone would therefore delete the banner off the OLD
 * card, before the return had even painted, and leave the admin looking at the
 * previous brand/last4 under an ACTIVE pill with nothing on screen saying a
 * change was in flight. That is strictly worse than the hand-dismissal this
 * replaces, so the replacement path keeps hand-dismissal.
 *
 * `null` — no stash, so a cold load, a new tab/session or storage disabled — is
 * "unknown" and gets the same treatment. Never worse than today's behaviour is
 * the bar; guessing "no card" to widen the fix would fail the replacement case
 * exactly where it is least recoverable.
 *
 * A banner that never renders because the very first post-return read already
 * shows the card is the intended outcome on the add path, not a missed one:
 * Lago learns the payment method from the Checkout webhook, so it *can* be true
 * immediately, and in that case the payment-method card below is already
 * reporting it.
 */
export function isCheckoutReturnConfirmed(
  billing: { hasPaymentMethod: boolean } | null | undefined,
  hadPaymentMethodBefore: boolean | null,
): boolean {
  if (hadPaymentMethodBefore !== false) return false
  return billing?.hasPaymentMethod === true
}
